import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import { loadLeadFinance, fmtZAR } from "./SessionFinanceTab";
import { costLineTotal, leadActivityIncome } from "@/lib/sessionPnl";
import { calculateItemTotalCost } from "@/lib/itineraryCostUtils";
import { toZAR } from "@/lib/budgetActualUtils";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { todaySAST } from "./sastToday";

export function LeadProfitSummary({ leadId }: { leadId: string }) {
  const { data: rates } = useExchangeRates();
  const { data, isLoading } = useQuery({
    queryKey: ["lead-finance-summary", leadId],
    queryFn: () => loadLeadFinance(leadId),
  });
  if (isLoading || !data) return <div className="flex justify-center p-4"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const quoteZar = toZAR(data.quoteTotalZar, data.currency, rates);
  const rows = data.items.map((it: any) => {
    const income = leadActivityIncome(calculateItemTotalCost(it), data.allItemsCost, quoteZar);
    const head = it.quantity || 1;
    const costs =
      data.costs.filter((c: any) => c.itinerary_item_id === it.id).reduce((s: number, c: any) => s + costLineTotal(c, head), 0) +
      data.actuals.filter((a: any) => a.itinerary_item_id === it.id).reduce((s: number, a: any) => s + toZAR(Number(a.amount), a.currency, rates), 0);
    return { it, income, costs, profit: income - costs };
  });
  const tot = rows.reduce((a, r) => ({ income: a.income + r.income, costs: a.costs + r.costs }), { income: 0, costs: 0 });
  const profit = tot.income - tot.costs;
  const today = todaySAST();
  const allDone = data.items.length > 0 && data.items.every((i: any) => (i.check_out || i.date || "") < today);

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-semibold">Profit by activity</h3>
        <Badge variant={allDone ? "default" : "secondary"}>{allDone ? "Final" : "Provisional"}</Badge>
      </div>
      {!data.quoteTotalZar && <p className="mb-2 text-xs text-muted-foreground">No accepted quote yet — income shows as zero.</p>}
      <div className="space-y-1 text-sm">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 text-xs uppercase text-muted-foreground">
          <span>Activity</span><span className="text-right">Income</span><span className="text-right">Costs</span><span className="text-right">Profit</span>
        </div>
        {rows.map(({ it, income, costs, profit }) => (
          <div key={it.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4">
            <span className="truncate">{it.date ? format(new Date(it.date), "dd MMM") + " · " : ""}{it.custom_title || it.title || "Untitled"}</span>
            <span className="text-right">{fmtZAR(income)}</span>
            <span className="text-right">{fmtZAR(costs)}</span>
            <span className={`text-right ${profit >= 0 ? "text-green-600" : "text-destructive"}`}>{fmtZAR(profit)}</span>
          </div>
        ))}
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 border-t pt-1 font-semibold">
          <span>Lead total {tot.income ? `· margin ${((profit / tot.income) * 100).toFixed(1)}%` : ""}</span>
          <span className="text-right">{fmtZAR(tot.income)}</span>
          <span className="text-right">{fmtZAR(tot.costs)}</span>
          <span className={`text-right ${profit >= 0 ? "text-green-600" : "text-destructive"}`}>{fmtZAR(profit)}</span>
        </div>
      </div>
    </Card>
  );
}
